import { FunctionComponent, useState } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Box, Button, Divider, Grid, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import StorageService from '../../services/storageService';
import { Address } from './online-order-entry-header';
import { useGetChannelPartnerOEntryHeaderCart, useGetChannelPartnerOEntryHeaderOrderSubmit, usePostChannelPartnerOEntryHeader, usePatchChannelPartnerOEntryHeaderCart } from '../../hooks/api/oms';
import moment from 'moment';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

const OnlineOrderEntryOrderSend: FunctionComponent = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');

  const oEntryHeader = StorageService.getOEntryHeader();
  const oEntryHeaderValue = StorageService.getOEntryHeaderValue();
  const {data: getCart } = useGetChannelPartnerOEntryHeaderCart();
  const {mutateAsync: createHeader} = usePostChannelPartnerOEntryHeader();
  const {mutateAsync: createOrder} = useGetChannelPartnerOEntryHeaderOrderSubmit();
  const {mutateAsync: updateCartItem} = usePatchChannelPartnerOEntryHeaderCart();
  
  const [disabled, setDisabled] = useState<boolean>(false);

  // Function to verify cart data after updates
  const verifyCartUpdates = () => {
    const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
    
    if (getCart?.items) {
      getCart.items.forEach((item: any) => {
        const localStorageDate = cartShipDates[item.partNumber];
      });
    }
  };

  // Function to update cart items with latest ship dates from localStorage
  const updateCartItemsWithLatestDates = async () => {
    const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
    const updatePromises = [];
    
    for (const [partNumber, shipDate] of Object.entries(cartShipDates)) {
      if (shipDate) {
        const formattedDate = moment(shipDate as string).format('MM/DD/YY');
        
        updatePromises.push(
          updateCartItem({
            partNumber,
            requestedShipDate: formattedDate
          }).then(result => {
            return result;
          }).catch(error => {
            throw error;
          })
        );
      }
    }
    
    if (updatePromises.length > 0) {
      try {
        await Promise.all(updatePromises);
        
        // Verify the updates were applied
        verifyCartUpdates();
      } catch (error) {
        // Error updating cart items
      }
    }
  };

  const shipToAddresses = oEntryHeader?.shipToAddresses?.find((address: Address) => address.ad_addr === oEntryHeaderValue?.shipTo) || {};
  const shipVia = oEntryHeader?.shipViaOptions?.find((option: any) => option.fr_list === oEntryHeaderValue?.shipViaCarrier) || {};
  const shippingTerms = oEntryHeader?.shipTermsOptions?.find((term: any) => term.ft_terms === oEntryHeaderValue?.shippingTerms) || {};
  const conDay = oEntryHeader?.consolidationDays?.find((day: any) => day.condayname === oEntryHeaderValue?.selectDay) || {};

  const handleNext = async () => {
    setDisabled(true);
    setError('');
    await updateCartItemsWithLatestDates(); // Call the new function here
    const result = await submitHeader();
    if (result?.orderId) {
      await submitOrder(result.orderId);
    }
  };

  const submitHeader = async () => {
    try {
      // Get the selected customer number from storage (for on-behalf users)
      // Only include custNum if it's not empty (on-behalf users)
      const selectedCustNum = StorageService.getOEntryAdminCustomer();
      const headerData: any = {
        shipTo: oEntryHeaderValue?.shipTo,
        poNum: oEntryHeaderValue?.purchaseOrderNumber,
        shipVia: oEntryHeaderValue?.shipViaCarrier,
        shipTerms: oEntryHeaderValue?.shippingTerms,
        shipAccNum: oEntryHeaderValue?.carrierAccountNo,
        oe: oEntryHeaderValue?.comments?.oe || false,
        dnp: oEntryHeaderValue?.comments?.doNotPartial || false,
        dnc: false,
        rss: oEntryHeaderValue?.comments?.expedite || false,
        nafta: false,
        con: oEntryHeaderValue?.comments?.con || false,
        conDay: conDay?.conday || '',
        commentsChange: true,
        headComments: oEntryHeaderValue?.otherComments && oEntryHeaderValue.otherComments.trim() !== '' ? oEntryHeaderValue.otherComments : ''
      };
      
      // Only add custNum if it's not empty (for on-behalf users who selected a channel partner)
      if (selectedCustNum && selectedCustNum.trim() !== '') {
        headerData.custNum = selectedCustNum;
      }
      
      const result = await createHeader(headerData);
      if (result?.status === 200) {
        setDisabled(false);
        return result.data;
      } else {
        setDisabled(false);
        setError(result?.data?.message || 'An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  }

  const submitOrder = async (orderId: string) => {
    try {
      const result = await createOrder({
        orderId: orderId
      })
      if (result?.status === 200) {
        setDisabled(false);
        
        // Clear all order entry data after successful order submission
        StorageService.clearCart();
        localStorage.removeItem('cart_ship_dates');
        localStorage.removeItem('cart_comments');
        
        navigate('/order-management-system/online-order-entry/order/send-confirm');
      } else {
        setDisabled(false);
        setError(result?.data?.message || 'An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      
      // Check for specific validation errors first (order capacity, etc.)
      if (err?.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
        const errorMessages = err.response.data.errors.map((error: any) => {
          // COMMENTED OUT: Order capacity error handling - matches production behavior (AddProduct2.asp)
          // Production system allows quantities above order cap without showing error messages
          // Order cap exceeded items are allowed to proceed without user notification
          /*
          if (error.message === 'Quantity exceeds order capacity' && error.item) {
            return `Part ${error.item.partNumber}: Quantity exceeds order capacity. Maximum allowed: ${error.item.orderCap}`;
          }
          */
          return error.message;
        });
        setError(errorMessages.join('; '));
      } else if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during order submission. Please try again.');
      }
    }
  }

  return (
    <div className="online-order-entry-page">
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Online Order Entry"} breadcrumbs={'Send Order'} activeMenu={'oms'}>
      <Grid container sx={{ padding: '0px 20px 20px 20px' }}>
        <Grid size={12}>
          <Typography variant="h6">
            Please verify that your order is correct, then press the{' '}
            <Typography component="span" sx={{ color: '#c3183c', fontSize: '1.25rem' }}>
              Send Order Button
            </Typography>.
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5' }}>
        <Grid container sx={{backgroundColor: '#c3183c', color: '#ffffff', padding: '10px 20px'}}>
          <Grid size={12}>
            <Typography variant="h6">Order Information</Typography>
          </Grid>
        </Grid>
        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={12}>
            <Typography variant="body1" color="#000000" sx={{ fontSize: '0.875rem' }}>Purchase Order No : {oEntryHeaderValue?.purchaseOrderNumber}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={6}>
            <Typography variant="body1" fontWeight="bold" color="#000000" sx={{ fontSize: '0.875rem' }}>Sold To:</Typography>
            <Typography variant="body1" color="#000000" sx={{ fontSize: '0.875rem' }}>
              {oEntryHeader?.billToInfo?.ad_name}
              <br />
              {`${oEntryHeader?.billToInfo?.ad_line1} ${oEntryHeader?.billToInfo?.ad_city}, ${oEntryHeader?.billToInfo?.ad_state} ${oEntryHeader?.billToInfo?.ad_zip}`}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="body1" fontWeight="bold" color="#000000" sx={{ fontSize: '0.875rem' }}>Ship To:</Typography>
            <Typography variant="body1" color="#000000" sx={{ fontSize: '0.875rem' }}>
              {shipToAddresses?.ad_name}
              <br />
              {`${shipToAddresses?.ad_line1} ${shipToAddresses?.ad_city}, ${shipToAddresses?.ad_state} ${shipToAddresses?.ad_zip}`}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={12}>
            <Typography variant="body1" fontWeight="bold" color="#000000" sx={{ fontSize: '0.875rem' }}>Shipping Details:</Typography>
            <Grid container spacing={2} mt={1}>
              <Grid size={4}>Ship Via Carrier: {shipVia?.fr_desc}</Grid>
              <Grid size={4}>Shipping Terms: {shippingTerms?.ft_desc}</Grid>
              {oEntryHeaderValue?.carrierAccountNo && oEntryHeaderValue.carrierAccountNo.trim() !== '' && (
                <Grid size={4}>Carrier Account No. {oEntryHeaderValue.carrierAccountNo}</Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        {(oEntryHeader?.customerComments?.length > 0 || 
          oEntryHeaderValue?.comments?.oe || 
          oEntryHeaderValue?.comments?.doNotPartial || 
          oEntryHeaderValue?.comments?.expedite || 
          oEntryHeaderValue?.comments?.con) && (
          <Grid container sx={{padding: '10px 20px'}}>
            <Grid size={12}>
              <Typography variant="body1" fontWeight="bold" color="#000000" sx={{ fontSize: '0.875rem' }}>Standard Comments:</Typography>
              
              {/* Show checkbox-selected standard comments first (matches legacy order) */}
              <>
                {
                  oEntryHeaderValue?.comments?.doNotPartial && (
                    <Typography variant="body1" color="#000000" mt={1} sx={{ fontSize: '0.875rem' }}>
                      DO NOT PARTIAL (SHIP ORDER WHEN ALL LINE ITEMS ARE AVAILABLE)  
                    </Typography>
                  )
                }
                {
                  oEntryHeaderValue?.comments?.expedite && (
                    <Typography variant="body1" color="#000000" mt={1} sx={{ fontSize: '0.875rem' }}>
                      EXPEDITE ORDER/RSS (ADDITIONAL CHARGE: GREATER OF $100 OR 20% OF ORDER)
                    </Typography>
                  )
                }
                {
                  oEntryHeaderValue?.comments?.con && (
                    <Typography variant="body1" color="#000000" mt={1} sx={{ fontSize: '0.875rem' }}>
                      CONSOLIDATE ORDER ON {oEntryHeaderValue?.selectDay?.toUpperCase()}
                    </Typography>
                  )
                }
              </>
              
              {/* Show OE customer comments after checkbox comments (matches legacy) */}
              {
                oEntryHeaderValue?.comments?.oe && oEntryHeader?.customerComments?.length > 0 && oEntryHeader?.customerComments?.map((comment: Record<string, string>, index: number) => (
                  Array.from({ length: 15 }, (_, i) => {
                    const key = `cmt_cmmt${i + 1}`;
                    const value = comment[key];
                    return value && value.trim() !== '.' ? (
                      <Grid size={12} key={`${index}-${i}`}>
                        <Typography variant="body1" color="#000000" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          {value}
                        </Typography>
                      </Grid>
                    ) : null;
                  })
                ))
              }
            </Grid>
          </Grid>
        )}
        <Divider sx={{ mt: 0 }} />

        {oEntryHeaderValue?.otherComments && oEntryHeaderValue.otherComments.trim() !== '' && (
          <Grid container sx={{padding: '10px 20px'}}>
            <Grid size={12}>
              <Typography variant="body1" fontWeight="bold" color="#000000" sx={{ fontSize: '0.875rem' }}>Other Comments:</Typography>
              <Typography variant="body1" color="#000000" mt={1} sx={{ fontSize: '0.875rem' }}>
                {oEntryHeaderValue.otherComments.toUpperCase()}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
      <Grid container sx={{padding: '10px 0px', mt: 1}}>
        <TableContainer component={Paper} variant="outlined" sx={{borderRadius: 0}}>
          <Table size="small" aria-label="inventory list table">
            <TableHead sx={{ backgroundColor: "#c3183c"}}>
              <TableRow>
                {['Part Number', 'Description', 'Request Date', 'Quantity', 'List Price', 'Extended List Price', 'Comments'].map((heading) => (
                  <TableCol key={heading} sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {heading}
                  </TableCol>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {getCart?.items?.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCol>{item.partNumber}</TableCol>
                  <TableCol>{item.description}</TableCol>
                  <TableCol>
                    {(() => {
                      // Priority 1: Check localStorage first for user-entered ship dates (before saving)
                      const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
                      const localStorageDate = cartShipDates[item.partNumber];
                      
                      if (localStorageDate) {
                        // Format the date from localStorage (YYYY-MM-DD) to MM-DD-YY for display
                        const momentDate = moment(localStorageDate);
                        if (momentDate.isValid()) {
                          return momentDate.format('MM-DD-YY');
                        }
                      }
                      
                      // Priority 2: Check API response (saved date from backend)
                      // Show saved date even when order cap exceeded
                      if (item.requestedShipDate) {
                        return item.requestedShipDate;
                      }
                      
                      // Priority 3: Check if quantity exceeds order cap - if so, show blank ship date (initial state)
                      if (item.orderCap && item.orderCap > 0 && item.quantity > item.orderCap) {
                        return ''; // Blank ship date when order cap exceeded (only if no saved date)
                      }
                      
                      return '';
                    })()}
                  </TableCol>
                  <TableCol>{item.quantity}</TableCol>
                   <TableCol>${parseFloat(item.listPrice).toFixed(2)}</TableCol>
                   <TableCol>${(item.listPrice * item.quantity) > 1000 ? (item.listPrice * item.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : (item.listPrice * item.quantity).toFixed(2)}</TableCol>
                   <TableCol>
                     {(() => {
                       // Check localStorage first for updated comments
                       const cartComments = JSON.parse(localStorage.getItem('cart_comments') || '{}');
                       const localStorageComment = cartComments[item.partNumber];
                       
                       // Priority: localStorage > API response
                       let comment = '';
                       if (localStorageComment) {
                         comment = localStorageComment;
                       } else {
                         comment = item.comments || item.comment || '-';
                       }
                       
                       // Convert to uppercase
                       return comment.toUpperCase();
                     })()}
                   </TableCol>
                 </TableRow>
              ))}
              <TableRow>
                <TableCol colSpan={7} align="right">
                  <Typography fontWeight="bold" color="#c3183c">
                    Order Sub Total: ${parseFloat(getCart?.subtotal || 0) > 1000 ? parseFloat(getCart?.subtotal || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : parseFloat(getCart?.subtotal || 0).toFixed(2)}
                  </Typography>
                </TableCol>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Grid size={12} sx={{ mt: 4 }}>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} onClick={handleNext}>
          SEND ORDER
        </Button>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} onClick={() => navigate('/order-management-system/online-order-entry/order')}>
          EDIT ORDER
        </Button>
      </Grid>
    </DashboardLayout>
    </div>
  )
}

export default OnlineOrderEntryOrderSend